import { AcceptedDocFileType } from '../shared/fileUtils';
import { AllowedFileTypeInQuestion } from './assignmentTypes';

export interface IFileAttachment {
  url: string;
  name: string; // file name
  type: AllowedFileTypeInQuestion;
  size: number; // in bytes
  mimeType: AcceptedDocFileType;
  key: string; // s3 key
}

export enum VideoProcessingStatus {
  UPLOADING = 'uploading',
  IN_QUEUE = 'in-queue',
  PREPARING = 'preparing',
  PROCESSING = 'processing',
  SAVING = 'saving',
  FINISHING = 'finishing',
}

export type VideoProcessingStatusType = `${VideoProcessingStatus}`;

export const VIDEO_PROCESSING_STATUS_TITLE: Record<
  VideoProcessingStatusType,
  string
> = {
  [VideoProcessingStatus.UPLOADING]: 'Uploading',
  [VideoProcessingStatus.IN_QUEUE]: 'In queue',
  [VideoProcessingStatus.PREPARING]: 'Preparing file',
  [VideoProcessingStatus.PROCESSING]: 'Processing file',
  [VideoProcessingStatus.SAVING]: 'Saving',
  [VideoProcessingStatus.FINISHING]: 'Finishing up',
};

export const VIDEO_PROCESSING_STATUS_ORDER: Record<
  VideoProcessingStatusType,
  number
> = {
  [VideoProcessingStatus.UPLOADING]: 1,
  [VideoProcessingStatus.IN_QUEUE]: 2,
  [VideoProcessingStatus.PREPARING]: 3,
  [VideoProcessingStatus.PROCESSING]: 4,
  [VideoProcessingStatus.SAVING]: 5,
  [VideoProcessingStatus.FINISHING]: 6,
};

// Transcoding job types
export enum TranscodingJobStatus {
  QUEUED = 'queued',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export type TranscodingJobStatusType = `${TranscodingJobStatus}`;

export enum TranscodingContentType {
  CHAPTER = 'chapter',
  POST = 'post',
  MANGO = 'mango',
}

export type TranscodingContentTypeType = `${TranscodingContentType}`;

export interface IBaseTranscodingJob {
  startedAt?: string; // ISO date-time string
  status: TranscodingJobStatusType;
  endedAt?: string; // ISO date-time string
  duration?: number; // Duration in seconds
}

export interface IWebhookTranscodingJob extends IBaseTranscodingJob {
  callbackUrl?: string;
  response?: Record<string, any>; // Webhook response can be any object
}

export interface ITranscodingJobDetails {
  createdAt: string; // ISO date-time string
  updatedAt: string; // ISO date-time string
  _id: string;
  inputFilePath: string;
  outputFilePath?: string;
  status: TranscodingJobStatusType;
  jobStartedAt?: string; // ISO date-time string
  jobCompletedAt?: string; // ISO date-time string
  jobExecutionTime?: number; // Total execution time in seconds
  downloadJob?: IBaseTranscodingJob;
  transcodingJob?: IBaseTranscodingJob;
  uploadJob?: IBaseTranscodingJob;
  cleanupJob?: IBaseTranscodingJob;
  webhookJob?: IWebhookTranscodingJob;
  contentType?: TranscodingContentTypeType;
}

export enum TranscodingJobEvent {
  STARTED = 'started', // when the transcoding job starts
  DOWNLOADING = 'downloading', // when the file is being downloaded for transcoding (VideoProcessingStatus.PREPARING)
  TRANSCODING = 'transcoding', // when the file is being transcoded (VideoProcessingStatus.PROCESSING)
  UPLOADING = 'uploading', // when the file is being uploaded after it has been transcoded (VideoProcessingStatus.SAVING)
  CLEANING_UP = 'cleaningup', // when the file is being cleaned up (VideoProcessingStatus.FINISHING)
  WEBHOOK_INVOKE = 'webhook-invoke',
  COMPLETED = 'completed', // if receiving this event, then there's nothing to listen
  FAILED = 'failed', // if receiving this event, then there's nothing to listen
}

export type TranscodingJobEventType = `${TranscodingJobEvent}`;

export interface ITranscodingJobEventDetails {
  event: TranscodingJobEventType;
  progress?: number; // in percentage (0-100%)
  estimatedTimeLeftBeforeCompletion?: number; // in seconds
  estimatedTimeOfCompletion?: number; // in seconds
}

export type TranscodingJobEventVideoProcessingStatusType =
  | TranscodingJobEvent.STARTED
  | TranscodingJobEvent.UPLOADING
  | TranscodingJobEvent.TRANSCODING
  | TranscodingJobEvent.DOWNLOADING
  | TranscodingJobEvent.CLEANING_UP
  | TranscodingJobEvent.WEBHOOK_INVOKE
  | TranscodingJobEvent.COMPLETED;

export const TRANSCODING_JOB_EVENT_VIDEO_PROCESSING_STATUS_MAP: Record<
  TranscodingJobEventVideoProcessingStatusType,
  VideoProcessingStatusType
> = {
  [TranscodingJobEvent.STARTED]: VideoProcessingStatus.PREPARING,
  [TranscodingJobEvent.DOWNLOADING]: VideoProcessingStatus.PREPARING,
  [TranscodingJobEvent.TRANSCODING]: VideoProcessingStatus.PROCESSING,
  [TranscodingJobEvent.UPLOADING]: VideoProcessingStatus.SAVING,
  [TranscodingJobEvent.CLEANING_UP]: VideoProcessingStatus.FINISHING,
  [TranscodingJobEvent.WEBHOOK_INVOKE]: VideoProcessingStatus.FINISHING,
  [TranscodingJobEvent.COMPLETED]: VideoProcessingStatus.FINISHING,
};

export enum EUploadModuleType {
  COURSE_COVER = 'course_cover',
  COURSE_DEFAULT_THUMBNAIL = 'course_default_thumbnail',
  COURSE_CHAPTER_THUMBNAIL = 'course_chapter_thumbnail',
  COURSE_QUESTION = 'course_question',
  COURSE_QUESTION_ANSWER = 'course_question_answer',
  COURSE_CHAPTER_RESOURCE = 'course_chapter_resource',
  COURSE_SUBTITLE = 'course_subtitle',
  COURSE_BULK_UPLOAD = 'course_bulk_upload',
  COURSE_ASSIGNMENT_QUESTION = 'course_assignment_question',
  COURSE_ASSIGNMENT_SUBMISSION = 'course_assignment_submission',

  COMMUNITY = 'community',
  COMMUNITY_POST = 'community_post',
  COMMUNITY_MESSAGE = 'community_message',
  COMMUNITY_REPLY = 'community_reply',

  MESSAGE = 'message',
  POST = 'post',

  TQ_CONNECT_SERVICE_REQUEST = 'tq_connect_service_request',
  TQ_CONNECT_SERVICE_REQUEST_RESPONSE = 'tq_connect_service_request_response',
  TQ_CONNECT_CHAT_MESSAGE = 'tq_connect_chat_message',
  TQ_CONNECT_SHOUTOUT = 'tq_connect_shoutout',

  SCHEDULED_POST = 'scheduled_post',

  PROFILE = 'profile',
  ACCOUNT_KYC = 'account_kyc',
  STOREFRONT = 'storefront',
  GAMIFICATION = 'gamification',
  EDITOR_INLINE = 'editor_inline',
  TAGMANGO_CLOUD = 'tagmango_cloud',
  BANNER = 'banner',
}
